/**
 * Start the catalogue search a cold `/?q=…` load is about to ask for BEFORE
 * React hydrates the page, instead of after. MiLicita only.
 *
 * ## Why (measured on milicita.es, 2026-10-20, cold Chromium on the droplet)
 *
 * `/?q=limpieza` is served the prerendered HOME (nginx has one file per path),
 * so the search cannot start until the bundle has loaded AND hydration has
 * rendered twice — once with the prerender's empty query string
 * (`use-shell-search-params.ts`), then with the real one. The first API request
 * left the browser ~1.0 s after navigation, ~0.5 s of it spent hydrating a
 * 150 KB page, and the results were on screen ~0.25 s after the search
 * answered. Starting the request when the client entry runs takes the hydration
 * time off the critical path; on a phone, where hydrating takes seconds, it
 * takes more.
 *
 * ## Why it cannot change what is shown
 *
 * The request is the one {@link Catalog} would send — same endpoint, same query
 * string (pinned by `early-search.test.tsx` against what the catalogue really
 * sends), same headers (`startEarlyRequest`). It is only ever taken by an
 * `apiRaw` call for byte-for-byte the same path; a URL this does not
 * understand is left alone, and an unused or failed early request costs one
 * request and nothing else.
 *
 * Narrow on purpose: only the plain catalogue (`/`, `/catalog`) with a typed
 * `q` and no other filter, and only for a visitor without a stored token (a
 * guest — the ad traffic this was measured for). Anything else keeps the
 * normal path.
 */
import { countryConfig } from '../country';
import { DEFAULT_LOCALE } from '../i18n';
import { getToken, startEarlyRequest } from './api';
import { buildSearchQuery } from './catalog-client';

/** Countries that start the search early. Spain (MiLicita) and Chile (Liciora):
 *  UZ and KZ keep their boot sequence exactly as it is. */
const EARLY_SEARCH_COUNTRIES: readonly string[] = ['es', 'cl'];

/** The catalogue's page size (`PAGE_SIZE` in routes/public/catalog.tsx). */
const CATALOG_PAGE_SIZE = 20;

/** Query parameters that never reach the catalogue's API request — ad/click
 *  tracking only. Any OTHER parameter is a filter, and the guess is skipped. */
function isTrackingParam(key: string): boolean {
  return key.startsWith('utm_') || key === 'fbclid' || key === 'gclid';
}

/**
 * The API path a cold load of `pathname?search` will search with, or `null`
 * when this is not a plain catalogue search this module predicts.
 *
 * Mirrors `CatalogListing` for its simplest case: no section (`apiScope` is
 * `all` because a typed query spans every layer), first page, default locale
 * (MiLicita has one locale and it owns the bare paths).
 */
export function earlySearchPath(pathname: string, search: string): string | null {
  if (pathname !== '/' && pathname !== '/catalog') return null;
  const params = new URLSearchParams(search);
  const q = params.get('q');
  if (!q || !q.trim()) return null;
  for (const key of params.keys()) {
    if (key !== 'q' && !isTrackingParam(key)) return null;
  }
  return `/catalog/search?${buildSearchQuery({
    q,
    scope: 'all',
    offset: 0,
    limit: CATALOG_PAGE_SIZE,
    lang: DEFAULT_LOCALE,
  })}`;
}

/** Called once by the client entry, before mounting. No-op outside MiLicita. */
export function startEarlySearch(): void {
  if (!EARLY_SEARCH_COUNTRIES.includes(countryConfig.code)) return;
  if (typeof window === 'undefined') return;
  if (getToken() != null) return;
  const path = earlySearchPath(window.location.pathname, window.location.search);
  if (path) startEarlyRequest(path);
}
